import { useState } from "react";
import { DataTable } from "../components/DataTable";

type GiftCard = {
  id: string;
  code: string;
  value: number;
  balance: number;
  issuedTo: string;
  issuedOn: string;
  expiryDate: string;
  status: "Active" | "Used" | "Expired" | "Disabled";
};

function genCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "GC-";
  for (let i = 0; i < 4; i++) {
    for (let j = 0; j < 4; j++) code += chars[Math.floor(Math.random() * chars.length)];
    if (i < 3) code += "-";
  }
  return code;
}

const initialCards: GiftCard[] = [
  { id: "gc1", code: "GC-ABCD-EFGH-IJKL", value: 1000, balance: 1000, issuedTo: "Priya Sharma",  issuedOn: "2026-05-10", expiryDate: "2026-11-10", status: "Active"   },
  { id: "gc2", code: "GC-MNOP-QRST-UVWX", value: 2500, balance: 0,    issuedTo: "Anjali Mehta",  issuedOn: "2026-04-15", expiryDate: "2026-10-15", status: "Used"     },
  { id: "gc3", code: "GC-YZAB-CDEF-GHIJ", value: 500,  balance: 500,  issuedTo: "Sunita Rao",    issuedOn: "2026-03-01", expiryDate: "2026-03-31", status: "Expired"  },
  { id: "gc4", code: "GC-KLMN-OPQR-STUV", value: 5000, balance: 3200, issuedTo: "Meera Joshi",   issuedOn: "2026-05-18", expiryDate: "2026-11-18", status: "Active"   },
  { id: "gc5", code: "GC-WXYZ-1234-5678", value: 1500, balance: 1500, issuedTo: "Kavya Nair",    issuedOn: "2026-05-05", expiryDate: "2026-11-05", status: "Disabled" },
  { id: "gc6", code: "GC-9ABC-DEFS-GHIJ", value: 3000, balance: 1800, issuedTo: "Divya Singh",   issuedOn: "2026-05-01", expiryDate: "2026-10-31", status: "Active"   },
];

const AMOUNTS = [500, 1000, 2500, 5000, 10000];

const STATUS_META: Record<GiftCard["status"], { color: string; bg: string; icon: string }> = {
  Active:   { color: "#16a34a", bg: "#dcfce7", icon: "bi-check-circle-fill" },
  Used:     { color: "#7a6652", bg: "#f0ebe4", icon: "bi-check2-all" },
  Expired:  { color: "#dc2626", bg: "#fee2e2", icon: "bi-clock-history" },
  Disabled: { color: "#64748b", bg: "#f1f5f9", icon: "bi-slash-circle-fill" },
};

const STYLES = `
  .gc-stats { display:grid; grid-template-columns:repeat(auto-fit,minmax(130px,1fr)); gap:14px; margin-bottom:4px; }
  .gc-stat { background:#fff; border:1px solid #f0ebe4; border-radius:16px; padding:16px 18px; box-shadow:0 2px 8px rgba(26,18,8,.06); }
  .gc-stat-lbl { font-size:11px; font-weight:700; color:#9b8878; text-transform:uppercase; letter-spacing:.6px; margin-bottom:5px; display:flex; align-items:center; gap:5px; }
  .gc-stat-val { font-size:20px; font-weight:800; color:#1a1208; }

  .gc-create-card { background:#fff; border:1px solid #f0ebe4; border-radius:16px; box-shadow:0 2px 12px rgba(26,18,8,.07); overflow:hidden; }
  .gc-create-head { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; background:linear-gradient(135deg,#1a1208 0%,#2d1e0f 100%); }
  .gc-create-title { font-size:14px; font-weight:800; color:#fff; display:flex; align-items:center; gap:8px; }
  .gc-create-body { padding:18px 20px; }
  .gc-form-row { display:flex; gap:12px; flex-wrap:wrap; align-items:flex-end; }
  .gc-field { display:flex; flex-direction:column; gap:4px; }
  .gc-label { font-size:11px; font-weight:700; color:#7a6652; text-transform:uppercase; letter-spacing:.6px; }
  .gc-input { padding:8px 12px; border:1.5px solid #ede6df; border-radius:10px; font-size:13px; color:#1a1208; font-family:inherit; outline:none; background:#fdf8f3; transition:border .2s; min-width:0; }
  .gc-input:focus { border-color:var(--theme); background:#fff; }

  .gc-amount-chips { display:flex; gap:6px; flex-wrap:wrap; }
  .gc-chip { padding:6px 14px; border-radius:20px; border:1.5px solid #ede6df; background:#fdf8f3; font-size:12px; font-weight:700; color:#7a6652; cursor:pointer; font-family:inherit; transition:all .15s; }
  .gc-chip.selected { border-color:var(--theme); background:var(--theme-light); color:var(--theme); }

  .gc-gen-btn { padding:9px 20px; border-radius:10px; border:none; background:linear-gradient(135deg,#d4551a,var(--theme)); color:#fff; font-size:13px; font-weight:700; cursor:pointer; font-family:inherit; display:flex; align-items:center; gap:7px; white-space:nowrap; box-shadow:0 4px 12px var(--theme-glow); }
  .gc-gen-btn:hover { filter:brightness(1.08); transform:translateY(-1px); }

  .gc-table-card { background:#fff; border:1px solid #f0ebe4; border-radius:16px; box-shadow:0 2px 12px rgba(26,18,8,.07); overflow:hidden; }
  .gc-table-head { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; background:linear-gradient(135deg,#1a1208 0%,#2d1e0f 100%); flex-wrap:wrap; gap:8px; }
  .gc-table-title { font-size:14px; font-weight:800; color:#fff; display:flex; align-items:center; gap:8px; }
  .gc-filter-sel { padding:6px 10px; border:1.5px solid rgba(255,255,255,.15); border-radius:8px; background:rgba(255,255,255,.07); color:#fff; font-size:12px; font-family:inherit; outline:none; }
  .gc-filter-sel option { background:#1a1208; }

  .gc-code { font-family:monospace; font-size:12px; font-weight:700; color:#1a1208; background:#f5f0eb; padding:3px 8px; border-radius:6px; letter-spacing:.5px; }
  .gc-status-badge { display:inline-flex; align-items:center; gap:5px; padding:3px 9px; border-radius:20px; font-size:11.5px; font-weight:700; }

  .gc-bal-bar-wrap { width:70px; background:#f0ebe4; border-radius:3px; height:5px; overflow:hidden; display:inline-block; margin-right:5px; vertical-align:middle; }
  .gc-bal-bar { height:100%; border-radius:3px; background:linear-gradient(90deg,var(--theme),#e05a28); }

  .gc-disable-btn { padding:4px 10px; border-radius:8px; border:1.5px solid #ede6df; background:#fdf8f3; color:#64748b; font-size:11px; font-weight:700; cursor:pointer; font-family:inherit; transition:all .15s; }
  .gc-disable-btn:hover { background:#ef4444; color:#fff; border-color:#ef4444; }
  .gc-disable-btn:disabled { opacity:.35; cursor:not-allowed; }

  .gc-toast { position:fixed; bottom:24px; right:24px; background:#1a1208; color:#fff; padding:12px 20px; border-radius:12px; font-size:13px; font-weight:600; z-index:9999; animation:gcToastIn .3s ease; box-shadow:0 8px 24px rgba(0,0,0,.25); display:flex; align-items:center; gap:8px; }
  @keyframes gcToastIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
`;

export function GiftCardsPage() {
  const [cards, setCards] = useState<GiftCard[]>(initialCards);
  const [filterStatus, setFilterStatus] = useState("All");
  const [selectedAmt, setSelectedAmt] = useState<number>(1000);
  const [issuedTo, setIssuedTo] = useState("");
  const [expiryDays, setExpiryDays] = useState("180");
  const [toast, setToast] = useState("");

  const filtered = cards.filter(c => filterStatus === "All" || c.status === filterStatus);

  const totalIssued  = cards.reduce((s, c) => s + c.value, 0);
  const totalBalance = cards.reduce((s, c) => s + c.balance, 0);
  const activeCount  = cards.filter(c => c.status === "Active").length;
  const usedValue    = cards.filter(c => c.status === "Used").reduce((s, c) => s + c.value, 0);

  function createCard() {
    if (!issuedTo) return;
    const today = new Date();
    const expiry = new Date(today.getTime() + Number(expiryDays) * 86400000);
    const fmt = (d: Date) => d.toISOString().split("T")[0];
    const card: GiftCard = {
      id: `gc${Date.now()}`,
      code: genCode(),
      value: selectedAmt,
      balance: selectedAmt,
      issuedTo,
      issuedOn: fmt(today),
      expiryDate: fmt(expiry),
      status: "Active",
    };
    setCards(prev => [card, ...prev]);
    setIssuedTo("");
    setToast(`Gift card ${card.code} issued to ${card.issuedTo}`);
    setTimeout(() => setToast(""), 3500);
  }

  function disableCard(id: string) {
    setCards(prev => prev.map(c => c.id === id ? { ...c, status: "Disabled" } : c));
  }

  const cols = [
    { key: "code",       label: "Code",        render: (row: GiftCard) => <span className="gc-code">{row.code}</span> },
    { key: "issuedTo",   label: "Issued To",   render: (row: GiftCard) => row.issuedTo },
    { key: "value",      label: "Value",       render: (row: GiftCard) => `₹${row.value.toLocaleString("en-IN")}` },
    { key: "balance",    label: "Balance",     render: (row: GiftCard) => (
      <div style={{ display: "flex", alignItems: "center" }}>
        <div className="gc-bal-bar-wrap"><div className="gc-bal-bar" style={{ width: `${(row.balance/row.value)*100}%` }}></div></div>
        <strong>₹{row.balance.toLocaleString("en-IN")}</strong>
      </div>
    )},
    { key: "expiryDate", label: "Expires",     render: (row: GiftCard) => row.expiryDate },
    { key: "status",     label: "Status",      render: (row: GiftCard) => {
      const s = STATUS_META[row.status];
      return <span className="gc-status-badge" style={{ color: s.color, background: s.bg }}><i className={`bi ${s.icon}`}></i>{row.status}</span>;
    }},
    { key: "id", label: "Action", render: (row: GiftCard) =>
      <button className="gc-disable-btn" disabled={row.status !== "Active"} onClick={() => disableCard(row.id)}>
        <i className="bi bi-slash-circle"></i> Disable
      </button>
    },
  ];

  return (
    <div className="d-grid gap-3">
      <style>{STYLES}</style>

      {/* Stats */}
      <div className="gc-stats">
        {[
          { lbl: "Total Issued",   val: `₹${(totalIssued/1000).toFixed(0)}K`,   icon: "bi-gift-fill",      color: "#ec4899" },
          { lbl: "Total Balance",  val: `₹${(totalBalance/1000).toFixed(1)}K`,  icon: "bi-wallet2",        color: "#059669" },
          { lbl: "Active Cards",   val: activeCount,                              icon: "bi-check-circle-fill", color: "#2563eb" },
          { lbl: "Redeemed Value", val: `₹${(usedValue/1000).toFixed(0)}K`,     icon: "bi-check2-all",     color: "#7c3aed" },
        ].map(s => (
          <div key={s.lbl} className="gc-stat">
            <div className="gc-stat-lbl"><i className={`bi ${s.icon}`} style={{ color: s.color }}></i>{s.lbl}</div>
            <div className="gc-stat-val">{s.val}</div>
          </div>
        ))}
      </div>

      {/* Create Card */}
      <div className="gc-create-card">
        <div className="gc-create-head">
          <div className="gc-create-title"><i className="bi bi-gift-fill"></i> Generate Gift Card</div>
        </div>
        <div className="gc-create-body">
          <div className="d-grid gap-3">
            <div>
              <div className="gc-label" style={{ marginBottom: 8 }}>Select Amount</div>
              <div className="gc-amount-chips">
                {AMOUNTS.map(amt => (
                  <button key={amt} className={`gc-chip ${selectedAmt === amt ? "selected" : ""}`} onClick={() => setSelectedAmt(amt)}>
                    ₹{amt.toLocaleString("en-IN")}
                  </button>
                ))}
              </div>
            </div>
            <div className="gc-form-row">
              <div className="gc-field" style={{ flex: 2, minWidth: 180 }}>
                <div className="gc-label">Issue To (Customer Name / Email)</div>
                <input className="gc-input" placeholder="e.g. Priya Sharma" value={issuedTo} onChange={e => setIssuedTo(e.target.value)} />
              </div>
              <div className="gc-field" style={{ minWidth: 140 }}>
                <div className="gc-label">Valid For (Days)</div>
                <input className="gc-input" type="number" min="1" value={expiryDays} onChange={e => setExpiryDays(e.target.value)} />
              </div>
              <button className="gc-gen-btn" onClick={createCard} disabled={!issuedTo}>
                <i className="bi bi-magic"></i> Generate Card
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="gc-table-card">
        <div className="gc-table-head">
          <div className="gc-table-title"><i className="bi bi-grid-3x3-gap-fill"></i> Gift Cards ({filtered.length})</div>
          <select className="gc-filter-sel" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            {["All", "Active", "Used", "Expired", "Disabled"].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div style={{ padding: "0 4px" }}>
          <DataTable columns={cols} rows={filtered} rowKey={(r) => r.id} />
        </div>
      </div>

      {toast && (
        <div className="gc-toast">
          <i className="bi bi-gift-fill" style={{ color: "#f5c89a" }}></i>
          {toast}
        </div>
      )}
    </div>
  );
}
